import React, { useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PropCell, StatusPill, BORDER, MUTED, TEXT } from "../../components/requestUi";
import {
  DesktopBack,
  DesktopButton,
  DesktopPage,
  DetailItem,
  DetailSection,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  deleteLeaveRequestApi,
  getLeaveRequestByIdApi,
} from "../../api/leave.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH, REQUEST_STATUS } from "../../config";

const LeaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["emp", "leave-detail", id],
    queryFn: () => getLeaveRequestByIdApi(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLeaveRequestApi(id),
    onSuccess: () => {
      toast.success("Đã xóa đơn nghỉ phép");
      queryClient.invalidateQueries({ queryKey: ["emp", "leave-list"] });
      navigate(`${EMP_BASE_PATH}/nghi-phep`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Xóa đơn thất bại")),
  });

  const canDelete = data?.status === REQUEST_STATUS.PENDING;

  const confirmDialog = (
    <ConfirmDialog
      isOpen={confirmDelete}
      title="Xóa đơn nghỉ phép"
      description="Bạn chắc chắn muốn xóa đơn nghỉ phép này?"
      confirmText="Xóa"
      cancelText="Hủy"
      danger
      loading={deleteMutation.isPending}
      onConfirm={() => {
        setConfirmDelete(false);
        deleteMutation.mutate();
      }}
      onClose={() => setConfirmDelete(false)}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage
        title="Chi tiết đơn nghỉ phép"
        right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep`)} />}
      >
        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <Text fontSize="14px" color={MUTED}>
            Không tải được đơn nghỉ phép.
          </Text>
        ) : (
          <DetailSection
            title={`Đơn ngày ${
              data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"
            }`}
            right={
              <Flex align="center" gap="12px">
                <StatusPill status={data.status} />
                {canDelete && (
                  <DesktopButton
                    variant="danger"
                    icon={<Box as={FiTrash2} fontSize="16px" />}
                    isLoading={deleteMutation.isPending}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Xóa đơn
                  </DesktopButton>
                )}
              </Flex>
            }
          >
            <DetailItem
              label="Ngày bắt đầu"
              value={data.startDate ? moment(data.startDate).format("DD/MM/YYYY") : "-"}
            />
            <DetailItem label="Số ngày nghỉ" value={data.daysRequested} />
            <DetailItem
              label="Loại nghỉ phép"
              value={data.leaveType?.name || data.leaveTypeCode}
            />
            <DetailItem label="Lý do" value={data.reason} span={3} />
          </DetailSection>
        )}

        {confirmDialog}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto">
      <AppBar title="Chi tiết đơn nghỉ phép" onBack={() => navigate(`${EMP_BASE_PATH}/nghi-phep`)} />

      <Box px="16px" pt="16px">
      {isLoading ? (
        <Loading />
      ) : isError || !data ? (
        <Text fontSize="14px" color={MUTED}>Không tải được đơn nghỉ phép.</Text>
      ) : (
        <Box bg="white" borderRadius="12px" border="1px solid" borderColor={BORDER} px="16px" pt="16px" pb="16px">
          <Flex align="center" gap="8px" flexWrap="wrap">
            <Text fontSize="14px" fontWeight={600} color={TEXT}>
              {data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"}
            </Text>
            <Text fontSize="14px" color={TEXT}>-</Text>
            <StatusPill status={data.status} />
          </Flex>

          <PropCell label="Ngày bắt đầu" value={data.startDate ? moment(data.startDate).format("DD/MM/YYYY") : "-"} />
          <PropCell label="Số ngày nghỉ" value={data.daysRequested} />
          <PropCell label="Loại nghỉ phép" value={data.leaveType?.name || data.leaveTypeCode} />
          <PropCell label="Lý do" value={data.reason} borderBottom={false} />

          {canDelete && (
            <Button
              mt="18px"
              w="100%"
              leftIcon={<FiTrash2 />}
              variant="outline"
              borderColor="#E5484D"
              color="#E5484D"
              _hover={{ bg: "#FFF0F0" }}
              isLoading={deleteMutation.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              Xóa đơn
            </Button>
          )}
        </Box>
      )}
      </Box>

      {confirmDialog}
    </Box>
  );
};

export default LeaveDetail;
